import { useEffect } from "react";

const STORAGE_KEY = "loggedInUser";

// Operator stored in browser storage right now (null if signed out).
function readOperator() {
    try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY))?.operator ?? null;
    } catch {
        return null;
    }
}

/**
 * Keeps all open tabs on the same session.
 * - Another tab signs out  -> this tab goes to the login page.
 * - Another user signs in  -> this tab reloads with that user's permissions.
 *
 * The browser fires the storage event only in tabs other than the one that
 * made the change, so the signing-out tab keeps its own redirect. The
 * visibilitychange check covers tablets that freeze background tabs and may
 * miss the storage event.
 */
export default function useCrossTabAuthSync() {
    useEffect(() => {
        // Operator this tab loaded with; compared against storage on every change.
        const operatorAtLoad = readOperator();

        const syncWithStorage = () => {
            const current = readOperator();
            if (current === operatorAtLoad) return;

            if (current === null) {
                window.location.pathname = "/"; // signed out elsewhere
            } else {
                window.location.reload(); // different user signed in elsewhere
            }
        };

        const onStorage = (e) => {
            // e.key is null when storage is cleared entirely
            if (e.key === STORAGE_KEY || e.key === null) syncWithStorage();
        };

        const onVisible = () => {
            if (document.visibilityState === "visible") syncWithStorage();
        };

        window.addEventListener("storage", onStorage);
        document.addEventListener("visibilitychange", onVisible);
        return () => {
            window.removeEventListener("storage", onStorage);
            document.removeEventListener("visibilitychange", onVisible);
        };
    }, []);
}
